import type * as lazyIndex from "./lazyIndex";
import { LazyAction, LazyImport, LazyModule } from "owa-bundling";
import React from "react";
import getCoprincipalOrGlobalSettingsMailboxInfo from "./getCoprincipalOrGlobalSettingsMailboxInfo";

import type { MailboxInfo } from "owa-client-types";
import type { PlacesTenantSettingComplete } from "./types/PlacesTenantSettingCompleteCallback";

export { PlacesSettings } from "./types/PlacesSettings";

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(() => import(/* webpackChunkName: "PlacesSettings" */ "./lazyIndex"), { name: "PlacesSettings" });

const lazyIsLoading: LazyImport<typeof lazyIndex.isLoading, typeof lazyModule> = new LazyImport(lazyModule, m => m.isLoading);
const lazyHasFailed: LazyImport<typeof lazyIndex.hasFailed, typeof lazyModule> = new LazyImport(lazyModule, m => m.hasFailed);
const lazyIsPlacesWebAppEnabled: LazyImport<typeof lazyIndex.isPlacesWebAppEnabled, typeof lazyModule> = new LazyImport(lazyModule, m => m.isPlacesWebAppEnabled);
const lazyIsPlacesPremiumEnabled: LazyImport<typeof lazyIndex.isPlacesPremiumEnabled, typeof lazyModule> = new LazyImport(lazyModule, m => m.isPlacesPremiumEnabled);
const lazyIsPlacesCopilotEnabled: LazyImport<typeof lazyIndex.isPlacesCopilotEnabled, typeof lazyModule> = new LazyImport(lazyModule, m => m.isPlacesCopilotEnabled);
const lazyIsPlacesFinderEnabled: LazyImport<typeof lazyIndex.isPlacesFinderEnabled, typeof lazyModule> = new LazyImport(lazyModule, m => m.isPlacesFinderEnabled);
const lazyIsOpxPlacesFinderEnabled: LazyImport<typeof lazyIndex.isOpxPlacesFinderEnabled, typeof lazyModule> = new LazyImport(lazyModule, m => m.isOpxPlacesFinderEnabled);
export const lazyLoadPlacesSettings: LazyAction<typeof lazyIndex.loadPlacesSettings, typeof lazyModule> = new LazyAction(lazyModule, m => m.loadPlacesSettings);

export function isPlacesWebAppEnabled(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return !!lazyIsPlacesWebAppEnabled.tryImportForRender()?.(mailboxInfo);
}

export function isPlacesPremiumEnabled(mailboxInfo: MailboxInfo, useValueFromSetting?: boolean): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return !!lazyIsPlacesPremiumEnabled.tryImportForRender()?.(mailboxInfo, useValueFromSetting);
}

export function isPlacesCopilotEnabled(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return !!lazyIsPlacesCopilotEnabled.tryImportForRender()?.(mailboxInfo);
}

export function isPlacesFinderEnabled(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return !!lazyIsPlacesFinderEnabled.tryImportForRender()?.(mailboxInfo);
}

export function asyncIsPlacesFinderEnabled(mailboxInfo: MailboxInfo): Promise<boolean> {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    return lazyIsPlacesFinderEnabled
        .import()
        .then(isPlacesFinderEnabledFn => isPlacesFinderEnabledFn(mailboxInfo));
}

export function isOpxPlacesFinderEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    return (
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    lazyIsOpxPlacesFinderEnabled.tryImportForRender()?.(mailboxInfo));
}

export function asyncIsOpxPlacesFinderEnabled(mailboxInfo: MailboxInfo): Promise<boolean> {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    return lazyIsOpxPlacesFinderEnabled
        .import()
        .then(isOpxPlacesFinderEnabledFn => isOpxPlacesFinderEnabledFn(mailboxInfo));
}

export function isPlacesExploreEnabled(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    return isPlacesPremiumEnabled(mailboxInfo);
}

export function isPlacesSearchEnabled(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    return isPlacesPremiumEnabled(mailboxInfo);
}

function hasFailed(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return !!lazyHasFailed.tryImportForRender()?.(mailboxInfo);
}

function isLoading(mailboxInfo: MailboxInfo): boolean {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    return lazyIsLoading.tryImportForRender()?.(mailboxInfo) !== false;
}

function noAccess(mailboxInfo: MailboxInfo) {
    return !isPlacesWebAppEnabled(mailboxInfo);
}

export function usePlacesSettings(mailboxInfo: MailboxInfo, shouldUseMainThread?: boolean, onPlacesTenantSettingComplete?: PlacesTenantSettingComplete): {
    loading: boolean;
    failed: boolean;
    noAccess: boolean;
    retry: () => void;
} {
    mailboxInfo = getCoprincipalOrGlobalSettingsMailboxInfo(mailboxInfo);
    
    const loadPlacesSettings = React.useCallback(() => {
        lazyLoadPlacesSettings.importAndExecute(mailboxInfo, shouldUseMainThread, onPlacesTenantSettingComplete);
    }, [mailboxInfo, shouldUseMainThread, onPlacesTenantSettingComplete]);
    
    React.useEffect(() => {
        loadPlacesSettings();
    }, [loadPlacesSettings]);
    
    return {
        loading: isLoading(mailboxInfo),
        failed: hasFailed(mailboxInfo),
        noAccess: noAccess(mailboxInfo),
        retry: loadPlacesSettings,
    };
}
